import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { RoleLevelsField } from "@/components/roles/RoleLevelsField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type RoleLevelOption,
  useRoleLevelOptions,
  useSetRoleLevels,
} from "@/hooks/use-role-level-applicability";
import { useRoleLevels } from "@/hooks/use-role-levels";
import { type Role, useCreateRole, useUpdateRole } from "@/hooks/use-roles";
import { type RoleCreateInput, roleCreateSchema, roleStatusValues } from "@/schemas/roles";

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: Role;
}

export function RoleFormDialog({ open, onOpenChange, role }: RoleFormDialogProps) {
  const isEdit = role != null;
  const createRole = useCreateRole();
  const updateRole = useUpdateRole(role?.id ?? "");

  const setRoleLevels = useSetRoleLevels();

  // Career levels (WI-104) live in this form rather than a panel of their own.
  // On edit we need each level's `applies` + `peopleCount` for the role; on
  // create the role doesn't exist yet, so fall back to the bare catalogue.
  const { data: roleOptions = [], isLoading: optionsLoading } = useRoleLevelOptions(
    role?.id ?? "",
    isEdit,
  );
  const { data: catalog = [], isLoading: catalogLoading } = useRoleLevels();
  const levelOptions: RoleLevelOption[] = isEdit
    ? roleOptions
    : catalog
        .filter((l) => !l.retired)
        .map((l) => ({
          id: l.id,
          name: l.name,
          definition: l.definition,
          sortOrder: l.sortOrder,
          retired: l.retired,
          applies: false,
          peopleCount: 0,
          // The role doesn't exist yet on the create path, so nobody holds it.
          heldByViewer: false,
        }));
  const levelsLoading = isEdit ? optionsLoading : catalogLoading;

  const [levelIds, setLevelIds] = useState<Set<string>>(new Set());
  const initialLevelIds = useRef<Set<string>>(new Set());

  // Three generics, not one: the schema has `.default()` fields, so its INPUT
  // type has them optional while its OUTPUT type has them required. Passing
  // only the output type makes the resolver's own input type unassignable.
  const form = useForm<z.input<typeof roleCreateSchema>, unknown, RoleCreateInput>({
    resolver: zodResolver(roleCreateSchema),
    defaultValues: {
      name: role?.name ?? "",
      description: role?.description ?? "",
      pillarId: role?.pillarId ?? null,
      departmentId: role?.departmentId ?? null,
      roleTeamId: role?.roleTeamId ?? null,
      jobDescriptionUrl: role?.jobDescriptionUrl ?? "",
      status: role?.status ?? "draft",
    },
  });

  // Re-seed the form on each closed→open transition: "Add role" always starts
  // blank, "Edit" always reflects the current role. Without this the form keeps
  // whatever was last typed (react-hook-form captures defaultValues once at mount
  // and this dialog is a persistent instance), so a fresh "Add role" looked like
  // it was editing a prior role. The `wasOpen` guard means a background refetch
  // (new `role` reference while already open) re-runs the effect harmlessly and
  // never wipes in-progress edits.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      form.reset({
        name: role?.name ?? "",
        description: role?.description ?? "",
        pillarId: role?.pillarId ?? null,
        departmentId: role?.departmentId ?? null,
        roleTeamId: role?.roleTeamId ?? null,
        jobDescriptionUrl: role?.jobDescriptionUrl ?? "",
        status: role?.status ?? "draft",
      });
      levelsSeeded.current = false;
    }
    wasOpen.current = open;
  }, [open, role, form]);

  // Seed the level selection once per open, and only after the options land —
  // the fetch usually resolves *after* the dialog mounts, so seeding in the
  // reset effect above would capture an empty list. The `levelsSeeded` guard
  // means a background refetch can't wipe in-progress ticks.
  const levelsSeeded = useRef(false);
  useEffect(() => {
    if (!open || levelsSeeded.current || levelsLoading) return;
    const applied = new Set(levelOptions.filter((o) => o.applies).map((o) => o.id));
    setLevelIds(applied);
    initialLevelIds.current = applied;
    levelsSeeded.current = true;
  }, [open, levelsLoading, levelOptions]);

  const pillarId = form.watch("pillarId");
  const departmentId = form.watch("departmentId");

  /** Same membership, order-independent — skips a pointless write + audit row. */
  function levelsChanged() {
    const before = initialLevelIds.current;
    if (before.size !== levelIds.size) return true;
    for (const id of levelIds) if (!before.has(id)) return true;
    return false;
  }

  async function onSubmit(values: RoleCreateInput) {
    const payload = {
      ...values,
      description: values.description?.trim() || null,
      jobDescriptionUrl: values.jobDescriptionUrl || null,
    };

    if (isEdit && role) {
      // Levels FIRST, deliberately. The realistic failure here is the server
      // refusing to remove a level that still has people at it; doing levels
      // first means that rejection aborts before any field is written, so the
      // user sees the reason with nothing half-saved. `mutateAsync` throws on
      // failure, which leaves the dialog open with the error toast shown.
      if (levelsChanged()) {
        await setRoleLevels.mutateAsync({ roleId: role.id, roleLevelIds: [...levelIds] });
      }
      await updateRole.mutateAsync(payload);
    } else {
      // Create can't fail the people-guard (a brand-new role has nobody), so
      // the role must exist before its levels can be attached.
      const created = await createRole.mutateAsync(payload);
      const newId: unknown = created?.data?.id;
      if (typeof newId === "string" && levelIds.size > 0) {
        await setRoleLevels.mutateAsync({ roleId: newId, roleLevelIds: [...levelIds] });
      }
    }

    form.reset();
    onOpenChange(false);
  }

  const pending = createRole.isPending || updateRole.isPending || setRoleLevels.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Role" : "Create Role"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this role's details and lifecycle status."
              : "Define a new role for the catalog."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Two columns: role details on the left, career levels on the
                right. Collapses to a single stack below `md`, where the levels
                land underneath rather than being squeezed. */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="space-y-4 md:col-span-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g. Senior Software Engineer" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value ?? ""}
                          rows={3}
                          placeholder="Short summary of responsibilities"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="pillarId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pillar</FormLabel>
                        <EntityCombobox
                          entity="pillars"
                          value={field.value ?? null}
                          onChange={(v) => {
                            field.onChange(v);
                            // Clear downstream selections when the parent changes.
                            form.setValue("departmentId", null);
                            form.setValue("roleTeamId", null);
                          }}
                          includeNone
                          placeholder="None"
                          searchPlaceholder="Search pillars…"
                        />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="departmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department</FormLabel>
                        <EntityCombobox
                          entity="departments"
                          value={field.value ?? null}
                          onChange={(v) => {
                            field.onChange(v);
                            form.setValue("roleTeamId", null);
                          }}
                          filters={{ pillarId: pillarId ?? undefined }}
                          includeNone
                          placeholder="None"
                          searchPlaceholder="Search departments…"
                        />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="roleTeamId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role Team</FormLabel>
                        <EntityCombobox
                          entity="role-teams"
                          value={field.value ?? null}
                          onChange={field.onChange}
                          filters={{ departmentId: departmentId ?? undefined }}
                          includeNone
                          placeholder="None"
                          searchPlaceholder="Search role teams…"
                        />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="jobDescriptionUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job description URL</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          type="url"
                          placeholder="https://..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {roleStatusValues.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              {/* Right column — career levels (WI-104). */}
              <div className="md:col-span-1">
                <FormItem>
                  <FormLabel>Career levels</FormLabel>
                  <RoleLevelsField
                    options={levelOptions}
                    value={levelIds}
                    onChange={setLevelIds}
                    loading={levelsLoading}
                  />
                </FormItem>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
